import moment from "moment";
import { useState } from "react";
import DatePicker from "react-date-picker";
import { Authorization } from "../../common/AuthorizationCpt";
import { getErrorValueResponse } from "../../common/error/Error";
import loadingGreen from "../../images/loading_gray.gif";
import { HTTP_METHODS } from "../../utils/constants";
import {
  addDaysToDate,
  dateToISO,
  dateToRegularFormat,
} from "../../utils/dates";
import { networkBackendRequest } from "../../utils/networkUtils";
import { TimeslotsByDateConvert } from "../AdminAppointmentConfiguration/AdminAppointment.interface";
import Input from "../Input/Input";
import Select from "../Select/Select";
import Sidebar from "../Sidebar/Sidebar";
import {
  Container,
  InfoContainer,
  MainButton,
  RowContainer,
  SectionTitle,
  Text,
  Title,
} from "./AdminAppointmentConfigurationSidebarStyles";
interface Props {
  sidebarInfo?: TimeslotsByDateConvert;
  onOutsideClick: (value?: React.SetStateAction<boolean>) => void;
  rangeConfirmation: () => void;
  isEdit?: boolean;
}
function AdminAppointmentConfigurationSidebar(props: Props) {
  const { sidebarInfo, onOutsideClick, isEdit, rangeConfirmation } = props;
  const [startHour, updateStartHour] = useState(
    sidebarInfo ? sidebarInfo.timeslotStart?.slice(0, 5) : ""
  );
  const [endHour, updateEndHour] = useState(
    sidebarInfo ? sidebarInfo.timeslotEnd.slice(0, 5) : ""
  );
  const [dockNum, updateDockNum] = useState<any>(
    sidebarInfo ? sidebarInfo.docksNum : ""
  );
  const [capacity, updateCapacity] = useState<any>(
    sidebarInfo ? sidebarInfo.dockCapacity : ""
  );
  const [uploadDate, updateUploadDate] = useState(new Date());
  const [waitingForServerResponse, updateWaitingForServerResponse] =
    useState(false);
  const [isTimeslotCreated, updateIsTimeslotCreated] = useState(false);
  const [editionError, updateEditionError] = useState("");

  const isDateRestricted =
    sidebarInfo &&
    moment(sidebarInfo.timeslotDate) <
      moment(new Date(new Date().setHours(0, 0, 0, 0)));

  const createRequest = () => {
    if (parseInt(dockNum) < 1 || parseInt(capacity) < 1) return;
    if (waitingForServerResponse) return;
    updateWaitingForServerResponse(true);
    networkBackendRequest(
      "/timeslots-by-date/",
      HTTP_METHODS.POST,
      {},
      {
        date_end: dateToISO(addDaysToDate(uploadDate, 6)),
        date_start: dateToISO(uploadDate),
        timeslot_start: startHour,
        timeslot_end: endHour,
        docks_num: dockNum,
        dock_capacity: capacity,
      }
    )
      .then(() => {
        updateWaitingForServerResponse(false);
        onOutsideClick();
        rangeConfirmation();
      })
      .catch(() => {
        updateIsTimeslotCreated(true);
        updateWaitingForServerResponse(false);
      });
  };

  const editRequest = () => {
    if (parseInt(dockNum) < 1 || parseInt(capacity) < 1) return;
    if (waitingForServerResponse) return;
    updateWaitingForServerResponse(true);
    networkBackendRequest(
      `/timeslots-by-date/${sidebarInfo.id}/`,
      HTTP_METHODS.PATCH,
      null,
      {
        timeslot_start: startHour,
        timeslot_end: endHour,
        docks_num: dockNum,
        dock_capacity: capacity,
      }
    )
      .then(() => {
        rangeConfirmation();
        updateWaitingForServerResponse(false);
        onOutsideClick(true);
      })
      .catch((error) => {
        const errorMessage = getErrorValueResponse(error);
        updateEditionError(errorMessage);
        updateWaitingForServerResponse(false);
      });
  };

  const startHourOnChange = (ev) => {
    const newHour = ev.target.value;
    if (endHour !== "" && newHour >= endHour) {
      updateEndHour("");
    }
    updateStartHour(newHour);
  };

  const endHourOnChange = (ev) => {
    const newHour = ev.target.value;
    updateEndHour(newHour);
  };

  const uploadDateOnChange = (newDate) => {
    updateUploadDate(newDate);
  };

  const dockNumOnChange = (ev) => {
    const newDockNum = ev.target.value;
    if (!/^\d*$/.test(newDockNum)) return;
    if (parseInt(newDockNum) < 1) return;
    updateDockNum(newDockNum);
  };

  const capacityOnChange = (ev) => {
    const newCapacity = ev.target.value;
    if (!/^\d*$/.test(newCapacity)) return;
    if (parseInt(newCapacity) < 1) return;
    updateCapacity(newCapacity);
  };

  const isButtonDisabled = [startHour, endHour, dockNum, capacity].includes("");

  return (
    <Sidebar onOutsideClick={onOutsideClick}>
      <Container>
        <Authorization
          actions={[
            "timeslot-by-date.update_timeslot_by_date",
            "timeslot-by-date.create_timeslot_by_date",
          ]}
        >
          <div className="header">
            <Title>
              {isEdit
                ? "Editar parámetros de fecha de atención"
                : "Configurar planificación semanal"}
            </Title>
          </div>
          <InfoContainer className="content">
            <div>
              <SectionTitle light={true}>
                {isEdit ? "FECHA" : "INICIO DE SEMANA"}
              </SectionTitle>
              {isEdit ? (
                <Text>{dateToRegularFormat(sidebarInfo.timeslotDate)}</Text>
              ) : (
                <DatePicker
                  value={uploadDate}
                  onChange={uploadDateOnChange}
                  clearIcon={null}
                  dayPlaceholder={"dd"}
                  locale={"es-PE"}
                  monthPlaceholder={"mm"}
                  showLeadingZeros={true}
                  yearPlaceholder={"yy"}
                />
              )}
            </div>
            <RowContainer>
              <div>
                <SectionTitle light={true}>HORA INICIO</SectionTitle>
                <Select
                  className="clickable-element"
                  height="5rem"
                  width="20.6rem"
                  value={startHour}
                  onChange={startHourOnChange}
                >
                  <option disabled value="">
                    Seleccione
                  </option>
                  {getValidHours().map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <SectionTitle light={true}>HORA FIN</SectionTitle>
                <Select
                  className="clickable-element"
                  height="5rem"
                  width="20.6rem"
                  value={endHour}
                  onChange={endHourOnChange}
                >
                  <option disabled value="">
                    Seleccione
                  </option>
                  {getValidHours(startHour).map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </Select>
              </div>
            </RowContainer>
            <RowContainer>
              <div>
                <SectionTitle light={true}>DIQUES DISPONIBLES</SectionTitle>
                <Input
                  height="5rem"
                  width="20.6rem"
                  value={dockNum}
                  onChange={dockNumOnChange}
                />
              </div>
              <div>
                <SectionTitle light={true}>CAPACIDAD ASIGNADA</SectionTitle>
                <Input
                  height="5rem"
                  width="20.6rem"
                  value={capacity}
                  onChange={capacityOnChange}
                />
              </div>
            </RowContainer>
          </InfoContainer>
          <div className="footer">
            {isDateRestricted && (
              <div className="block-hint">{`Solo se pueden editar rangos horarios a partir del ${dateToRegularFormat(
                moment(new Date(new Date().setHours(0, 0, 0, 0))).toDate()
              )}`}</div>
            )}
            {isTimeslotCreated && (
              <div className="block-hint">
                {
                  "Los rangos horarios en este intervalo han sido creados previamente."
                }
              </div>
            )}
            {editionError && (
              <div className="block-hint">
                {
                  "No se puede reducir el rango horario ni las capacidades del mismo."
                }
              </div>
            )}
            <MainButton
              disabled={
                isButtonDisabled || isDateRestricted || waitingForServerResponse
              }
              onClick={isEdit ? editRequest : createRequest}
            >
              {waitingForServerResponse && (
                <img
                  src={loadingGreen}
                  width="50"
                  height="50"
                  alt="Use credit"
                />
              )}
              {isEdit ? "Guardar Cambios" : "Crear planificación"}
            </MainButton>
          </div>
        </Authorization>
      </Container>
    </Sidebar>
  );
}

function getValidHours(base?: any) {
  let firstTime = 0;

  if (base) {
    firstTime = Number(base.split(":")[0]) * 2 + (base[3] === "3" ? 1 : 0);
    firstTime++;
  }

  const hours = [];
  for (let time = firstTime; time < 48; time++) {
    let hour: any = Math.floor(time / 2);
    hour = hour < 10 ? `0${hour}` : `${hour}`;
    let minutes = time % 2 === 1 ? "30" : "00";
    hours.push(`${hour}:${minutes}`);
  }
  if (base) hours.push("23:59");
  return hours;
}

export default AdminAppointmentConfigurationSidebar;
